import React, { useEffect, useMemo, useState } from "react";
import { useAlert } from "react-alert";
import { useTranslation } from "react-i18next";

import { Button, SimpleInput } from "~/components";
import { useApi, useFetch } from "~/hooks";
import { useMe } from "~/services";

import { Container, FieldRow } from "./style";

const MIN_DAYS = 1;
const MAX_DAYS = 3650;

const parseSettingsPayload = (payload) => {
  if (payload == null || payload === "") return undefined;
  if (typeof payload === "object") {
    return payload;
  }
  if (typeof payload === "string") {
    try {
      return JSON.parse(payload);
    } catch {
      return undefined;
    }
  }
  return undefined;
};

const readDaysFromResponse = (fetchResponse) => {
  const rawRow = fetchResponse?.data ?? fetchResponse;
  const row = Array.isArray(rawRow) ? rawRow[0] : rawRow;
  if (! row || typeof row !== "object") return undefined;

  const inner = parseSettingsPayload(row.data);
  const raw =
    inner?.days_limit ??
    inner?.limit ??
    inner?.value ??
    inner?.days ??
    row.days_limit ??
    row.limit ??
    row.value ??
    row.days;

  if (raw === undefined || raw === null || raw === "") return undefined;
  const n = Number(raw);
  if (! Number.isFinite(n)) return undefined;
  return Math.trunc(n);
};

const ConstitutionDaysSettings = () => {
  const { t } = useTranslation("settings");
  const [daysInput, setDaysInput] = useState("");
  const [loading, setLoading] = useState(false);
  const api = useApi();
  const alert = useAlert();
  const { user, group } = useMe();
  const baseUrl = "/settings/empresa-constituicao-recente-days-limit";

  const clientId = group?.value;
  const groupId = user?.grupos?.[0]?.idgrupo;

  const fetchUrl = useMemo(() => {
    if (clientId) {
      return `${baseUrl}?client_id=${clientId}`;
    }
    if (groupId) {
      return `${baseUrl}?group_id=${groupId}`;
    }
    return null;
  }, [clientId, groupId, baseUrl]);

  const { data: fetchResponse } = useFetch({
    url: fetchUrl,
  });

  useEffect(() => {
    setDaysInput("");
  }, [fetchUrl]);

  useEffect(() => {
    if (fetchResponse == null) return;
    const parsed = readDaysFromResponse(fetchResponse);
    setDaysInput(parsed !== undefined ? String(parsed) : "");
  }, [fetchResponse]);

  const handleClick = async () => {
    const parsed = parseInt(daysInput, 10);
    if (
      ! Number.isFinite(parsed) ||
      parsed < MIN_DAYS ||
      parsed > MAX_DAYS
    ) {
      alert.error(
        t("constitution_days.validation_range", { min: MIN_DAYS, max: MAX_DAYS })
      );
      return;
    }

    setLoading(true);

    try {
      let payload = {
        days_limit: parsed,
      };

      if (clientId) {
        payload = {
          ...payload,
          client_id: clientId,
        };
      } else if (groupId) {
        payload = {
          ...payload,
          group_id: groupId,
        };
      }

      await api().post(baseUrl, payload);

      alert.success(t("constitution_days.success"));
    } catch (e) {
      alert.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <FieldRow>
        <SimpleInput
          name="constitution-days-limit"
          label={t("constitution_days.input_label")}
          value={daysInput}
          onChange={(e) => setDaysInput(e.target.value)}
          type="number"
          min={MIN_DAYS}
          max={MAX_DAYS}
          placeholder={t("constitution_days.placeholder")}
        />
        <Button
          onClick={handleClick}
          loading={loading}
          label={t("constitution_days.button")}
        />
      </FieldRow>
    </Container>
  );
};

export default ConstitutionDaysSettings;
